import { defineStore } from "pinia";
import {
    DynamicQuery, RegionTopQuery, TotalMetricDataQuery,
} from "@/types/analytics/requests";
import {
    DynamicResponse,
    RegionTopResponse, TotalMetricAccusedPersonsResponse,
    TotalMetricCasesSuspendedResponse,
    TotalMetricDataAcceptedCasesResponse, TotalMetricOvertimeCasesResponse, TotalMetricRehabilitatedPersonsResponse,
    TotalMetricReturnedCasesResponse
} from "@/types/analytics/responses";
import axios from "@/api/axios";
import {MapDisplayParamsKey} from "@/composables/constants/params";

export const useAnalyticStore = defineStore("AnalyticStore", {
    state: () => ({
        analyticMetricData: null,
        dynamicByMonth: null as DynamicResponse | null,
        topRegions: null as RegionTopResponse | null,
        loading: false,
    }),

    getters: {
        isLoading: (state) => state.loading,
    },

    actions: {
        /**
         * Получить значений метрик аналитики по выбранному параметру и процент к прошлому году за месяц.
         */
        async getAnalyticMetricData(query: TotalMetricDataQuery, param: MapDisplayParamsKey) {
            console.log(param);
            if (param === "cases_accepted") {
                // Получить значений метрик аналитики "Принято дел к производству" и процент к прошлому году за месяц.
                return this.getMetricAcceptedCases(query);
            } else if (param === "cases_sent_to_court_with_indictment") {
                // Получить значений метрик аналитики "Направлено прокурором в суд" и процент к прошлому году за месяц.
                return this.getMetricSentToCourt(query);
            } else if (param === "cases_suspended") {
                // Получить значений метрик аналитики "По приостановленным делам по п.п. 1,2,3 ч.1 ст. 208 УПК РФ" и процент к прошлому году за месяц.
                return this.getMetricCasesSuspended(query);
            } else if (param === "cases_returned_for_investigation") {
                // Получить значений метрик аналитики "Возвращено дел для дополнительного расследования прокурором и судом" и процент к прошлому году за месяц.
                return this.getMetricReturnedCases(query);
            } else if (param === "cases_completed_overtime") {
                // Получить значений метрик аналитики "Окончено дел в сроки свыше предусмотренного УПК РФ" и процент к прошлому году за месяц.
                return this.getMetricOvertimeCases(query);
            } else if (param === "accused_in_court_cases") {
                // Получить значений метрик аналитики "Число обвиняемых по направленным в суд делам" и процент к прошлому году за месяц.
                return this.getMetricAccusedPersons(query);
            } else if (param === "persons_rehabilitated") {
                // Получить значений метрик аналитики "Число оправданных и лиц, в отношении которых уголовное преследование прекращено судом по реабилитирующим основаниям (абсолютные цифры)" и процент к прошлому году за месяц.
                return this.getMetricRehabilitatedPersons(query);
            }
        },

        /**
         * GET /api/metrics/accepted-cases
         * Получить значений метрик аналитики "Принято дел к производству" и процент к прошлому году за месяц.
         */
        async getMetricAcceptedCases(query: TotalMetricDataQuery): Promise<TotalMetricDataAcceptedCasesResponse> {
            try {
                const response = await axios.get("/oracul/api/metrics/accepted-cases", { params: query });
                
                this.analyticMetricData = response.data;
                
                return response.data;
            } catch (error) {
                console.log(error);
                throw error;
            }
        },

        /**
         * GET /api/metrics/sent-to-court
         * Получить значений метрик аналитики "Направлено прокурором в суд" и процент к прошлому году за месяц.
         */
        async getMetricSentToCourt(query: TotalMetricDataQuery): Promise<TotalMetricDataAcceptedCasesResponse> {
            try {
                const response = await axios.get("/oracul/api/metrics/sent-to-court", { params: query });

                this.analyticMetricData = response.data;

                return response.data;
            } catch (error) {
                console.log(error);
                throw error;
            }
        },

        /**
         * GET /api/metrics/suspended-cases
         * Получить значений метрик аналитики "По приостановленным делам по п.п. 1,2,3 ч.1 ст. 208 УПК РФ" и процент к прошлому году за месяц.
         */
        async getMetricCasesSuspended(query: TotalMetricDataQuery): Promise<TotalMetricCasesSuspendedResponse> {
            try {
                const response = await axios.get("/oracul/api/metrics/suspended-cases", { params: query });

                this.analyticMetricData = response.data;

                return response.data;
            } catch (error) {
                console.log(error);
                throw error;
            }
        },

        /**
         * GET /api/metrics/returned-cases
         * Получить значений метрик аналитики "Возвращено дел для дополнительного расследования прокурором и судом" и процент к прошлому году за месяц.
         */
        async getMetricReturnedCases(query: TotalMetricDataQuery): Promise<TotalMetricReturnedCasesResponse> {
            try {
                const response = await axios.get("/oracul/api/metrics/returned-cases", { params: query });

                this.analyticMetricData = response.data;

                return response.data;
            } catch (error) {
                console.log(error);
                throw error;
            }
        },

        /**
         * GET /api/metrics/overtime-cases
         * Получить значений метрик аналитики "Окончено дел в сроки свыше предусмотренного УПК РФ" и процент к прошлому году за месяц.
         */
        async getMetricOvertimeCases(query: TotalMetricDataQuery): Promise<TotalMetricOvertimeCasesResponse> {
            try {
                const response = await axios.get("/oracul/api/metrics/overtime-cases", { params: query });

                this.analyticMetricData = response.data;

                return response.data;
            } catch (error) {
                console.log(error);
                throw error;
            }
        },

        /**
         * GET /api/metrics/accused-persons
         * Получить значений метрик аналитики "Число обвиняемых по направленным в суд делам" и процент к прошлому году за месяц.
         */
        async getMetricAccusedPersons(query: TotalMetricDataQuery): Promise<TotalMetricAccusedPersonsResponse> {
            try {
                const response = await axios.get("/oracul/api/metrics/accused-persons", { params: query });

                this.analyticMetricData = response.data;

                return response.data;
            } catch (error) {
                console.log(error);
                throw error;
            }
        },

        /**
         * GET /api/metrics/rehabilitated-persons
         * Получить значений метрик аналитики "Число оправданных и лиц, в отношении которых уголовное преследование прекращено судом по реабилитирующим основаниям (абсолютные цифры)" и процент к прошлому году за месяц.
         */
        async getMetricRehabilitatedPersons(query: TotalMetricDataQuery): Promise<TotalMetricRehabilitatedPersonsResponse> {
            try {
                const response = await axios.get("/oracul/api/metrics/rehabilitated-persons", { params: query });

                this.analyticMetricData = response.data;

                return response.data;
            } catch (error) {
                console.log(error);
                throw error;
            }
        },










        /**
         * GET /api/metrics/metric-district
         * Получить динамику значений метрики помесячно по России/Округам/Регионам
         */
        async getDynamicByMonth(query: DynamicQuery, type: "russia" | "districts" | "regions"): Promise<DynamicResponse> {
            try {
                const response = await axios.get(`/oracul/api/dynamic/${type}`, { params: query });

                this.dynamicByMonth = response.data;

                return response.data;
            } catch (error) {
                console.log("error");
                throw error;
            }
        },

        /**
         * GET /api/files/top-regions
         * Получить топ регионов по значению метрики за месяц
         */
        async getTopRegions(query: RegionTopQuery): Promise<RegionTopResponse> {
            try {
                const response = await axios.get("/oracul/api/files/top-regions",
                    { params: query }
                );

                this.topRegions = response.data;

                return response.data;
            } catch (error) {
                console.log("error");
                throw error;
            }
        },
    }
});